import { useState } from 'react';
import useSWR from 'swr';
import { toast } from 'sonner';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { get, post, ApiError } from '@/lib/api';
import { formatRelative } from '@/lib/time';
import {
  type School, type Coach, type HistoryEntry,
  qualityPct, readinessOf, coachesOf, stateOf, READINESS,
} from '@/lib/school';

// School detail drawer — Scout parity (workstream G): full record view,
// inline edit (merge-update via the scout proxy so scout-dashboard's cache
// invalidates), AD change + coach departure tracking, and coach/AD history.

type HistoryPayload = { coach_history: HistoryEntry[]; ad_history: HistoryEntry[] };

type Props = {
  open: boolean;
  onClose: () => void;
  placeId: string | null;
  // Revalidate the list after any write — quality/readiness/AD columns shift.
  onChanged: () => void;
};

function errMsg(e: unknown): string {
  if (e instanceof ApiError) {
    const body = e.body as { message?: string; error?: string } | null;
    return body?.message || body?.error || `Request failed (${e.status})`;
  }
  return e instanceof Error ? e.message : String(e);
}

// Ported from scout-dashboard's QualityBar: readiness-colored dot + 0–100 bar.
export function QualityBar({ school }: { school: School }) {
  const pct = qualityPct(school.data_quality_score);
  const r = READINESS[readinessOf(school)] || READINESS.not_ready;
  return (
    <div className="flex items-center gap-2">
      <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: r.dot }} />
      <div className="h-1 w-[60px] overflow-hidden rounded-full bg-bg">
        <div className="h-full rounded-full" style={{ width: `${pct}%`, background: r.dot }} />
      </div>
      <span className="font-mono text-[11px] tabular-nums text-muted">{pct}</span>
    </div>
  );
}

export function ReadinessBadge({ school }: { school: School }) {
  const r = READINESS[readinessOf(school)] || READINESS.not_ready;
  return <Badge className={r.cls}>{r.label}</Badge>;
}

function Section({ title, action, children }: { title: string; action?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="border-t border-divider pt-3">
      <div className="mb-2 flex items-center justify-between gap-2">
        <h3 className="text-[11px] uppercase tracking-wide text-muted">{title}</h3>
        {action}
      </div>
      {children}
    </div>
  );
}

function Row({ label, value }: { label: string; value?: React.ReactNode }) {
  return (
    <div className="flex justify-between gap-3 border-b border-divider py-1.5 text-[12px] last:border-b-0">
      <span className="shrink-0 text-muted">{label}</span>
      <span className="min-w-0 break-words text-right">{value || <span className="text-subtle">—</span>}</span>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-[11px] uppercase tracking-wide text-muted">{label}</span>
      {children}
    </label>
  );
}

function ExtLink({ href }: { href?: string }) {
  if (!href) return null;
  const url = /^https?:\/\//.test(href) ? href : `https://${href}`;
  return (
    <a href={url} target="_blank" rel="noreferrer" className="text-primary hover:underline">
      {href.replace(/^https?:\/\//, '').replace(/\/$/, '')}
    </a>
  );
}

// Editable subset of the school record. Name is included (typo fixes);
// state is not — it anchors the school's identity/filtering and is a
// scout-dashboard-level operation.
const EDIT_FIELDS = [
  'name', 'mascot', 'city', 'county', 'phone', 'website', 'athletic_website',
  'conference', 'district', 'primary_color', 'secondary_color',
] as const;
type EditField = (typeof EDIT_FIELDS)[number];
type EditForm = Record<EditField, string> & { ad_name: string; ad_email: string; ad_phone: string };

function formFrom(s: School): EditForm {
  const ad = s.athletic_director || {};
  return {
    name: s.name || '', mascot: s.mascot || '', city: s.city || '', county: s.county || '',
    phone: s.phone || '', website: s.website || '', athletic_website: s.athletic_website || '',
    conference: s.conference || '', district: s.district || '',
    primary_color: s.primary_color || '', secondary_color: s.secondary_color || '',
    ad_name: ad.name || '', ad_email: ad.email || '', ad_phone: ad.phone || '',
  };
}

export function SchoolDrawer({ open, onClose, placeId, onChanged }: Props) {
  const key = open && placeId ? `/api/scout/schools/${encodeURIComponent(placeId)}` : null;
  const { data: school, isLoading, mutate: mutateDetail } = useSWR<School>(key, get);
  const { data: history, mutate: mutateHistory } = useSWR<HistoryPayload>(key ? `${key}/history` : null, get);

  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<EditForm | null>(null);
  const [adFormOpen, setAdFormOpen] = useState(false);
  const [adForm, setAdForm] = useState({ name: '', email: '', phone: '', notes: '' });

  // Reset transient state when the drawer switches schools / closes.
  const [lastKey, setLastKey] = useState(key);
  if (key !== lastKey) {
    setLastKey(key);
    setEditing(false);
    setForm(null);
    setAdFormOpen(false);
  }

  const set = (k: keyof EditForm, v: string) => setForm((f) => (f ? { ...f, [k]: v } : f));

  async function saveEdit() {
    if (!school || !form || !placeId) return;
    const before = formFrom(school);
    const patch: Record<string, unknown> = {};
    for (const f of EDIT_FIELDS) {
      if (form[f].trim() !== before[f]) patch[f] = form[f].trim() || null;
    }
    if (form.ad_name.trim() !== before.ad_name || form.ad_email.trim() !== before.ad_email || form.ad_phone.trim() !== before.ad_phone) {
      // Whole-map write (Firestore update() replaces the map): spread the
      // existing AD so fields like source/started_at survive a contact edit.
      patch.athletic_director = form.ad_name.trim()
        ? { ...(school.athletic_director || {}), name: form.ad_name.trim(), email: form.ad_email.trim() || null, phone: form.ad_phone.trim() || null }
        : null;
    }
    if (!Object.keys(patch).length) { setEditing(false); return; }
    setBusy(true);
    try {
      await post(`/api/scout/schools/${encodeURIComponent(placeId)}/update`, patch);
      toast.success('School updated');
      setEditing(false);
      await mutateDetail();
      onChanged();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  }

  async function departCoach(c: Coach) {
    if (!placeId || !c.name) return;
    if (!confirm(`Record "${c.name}" as departed? They move to coach history.`)) return;
    setBusy(true);
    try {
      await post(`/api/scout/schools/${encodeURIComponent(placeId)}/coach-departure`, {
        coach_name: c.name,
        ...(c.sport_id ? { sport_id: c.sport_id } : {}),
      });
      toast.success(`${c.name} moved to history`);
      await Promise.all([mutateDetail(), mutateHistory()]);
      onChanged();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  }

  async function submitAdChange() {
    if (!placeId) return;
    setBusy(true);
    try {
      const body = {
        new_ad: adForm.name.trim()
          ? { name: adForm.name.trim(), email: adForm.email.trim() || null, phone: adForm.phone.trim() || null }
          : null,
        ...(adForm.notes.trim() ? { notes: adForm.notes.trim() } : {}),
      };
      await post(`/api/scout/schools/${encodeURIComponent(placeId)}/ad-change`, body);
      toast.success(adForm.name.trim() ? `AD changed to ${adForm.name.trim()}` : 'AD recorded as departed');
      setAdFormOpen(false);
      setAdForm({ name: '', email: '', phone: '', notes: '' });
      await Promise.all([mutateDetail(), mutateHistory()]);
      onChanged();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  }

  const ad = school?.athletic_director;
  const coaches = school ? coachesOf(school) : [];
  const historyEntries: { kind: 'Coach' | 'AD'; e: HistoryEntry }[] = [
    ...(history?.coach_history || []).map((e) => ({ kind: 'Coach' as const, e })),
    ...(history?.ad_history || []).map((e) => ({ kind: 'AD' as const, e })),
  ].sort((a, b) => String(b.e.departed_at || '').localeCompare(String(a.e.departed_at || '')));

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-[520px] overflow-y-auto sm:max-w-[520px]">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            {(school as { logo_url?: string } | null)?.logo_url && (
              <img
                src={(school as { logo_url?: string }).logo_url}
                alt=""
                className="h-9 w-9 shrink-0 rounded-[8px] border border-black/[0.06] bg-white object-contain"
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
              />
            )}
            {school?.primary_color && <span className="h-3.5 w-3.5 shrink-0 rounded-full border border-border" style={{ background: school.primary_color }} />}
            {school?.secondary_color && <span className="-ml-1 h-3.5 w-3.5 shrink-0 rounded-full border border-border" style={{ background: school.secondary_color }} />}
            <span className="truncate">{school?.name || 'School'}</span>
          </SheetTitle>
          {school && (
            <p className="text-[11px] text-muted">
              {[school.city, school.county ? `${school.county} County` : null, stateOf(school)].filter(Boolean).join(' · ')}
              {school.mascot ? ` · “${school.mascot}”` : ''}
            </p>
          )}
        </SheetHeader>

        {isLoading || !school ? (
          <div className="mt-6 space-y-2">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-32 w-full" />
          </div>
        ) : (
          <div className="mt-4 space-y-4">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2.5">
                <QualityBar school={school} />
                <ReadinessBadge school={school} />
              </div>
              {!editing && (
                <Button variant="secondary" size="sm" onClick={() => { setForm(formFrom(school)); setEditing(true); }}>
                  Edit
                </Button>
              )}
            </div>
            {(school.readiness_blockers?.length ?? 0) > 0 && (
              <p className="rounded-sm bg-warning/10 px-2.5 py-1.5 text-[11px] text-warning">
                Blockers: {school.readiness_blockers!.join(', ')}
              </p>
            )}

            {editing && form ? (
              <Section title="Edit school">
                <div className="grid grid-cols-2 gap-2.5">
                  <div className="col-span-2"><Field label="Name"><Input value={form.name} onChange={(e) => set('name', e.target.value)} /></Field></div>
                  <Field label="Mascot"><Input value={form.mascot} onChange={(e) => set('mascot', e.target.value)} /></Field>
                  <Field label="Phone"><Input value={form.phone} onChange={(e) => set('phone', e.target.value)} /></Field>
                  <Field label="City"><Input value={form.city} onChange={(e) => set('city', e.target.value)} /></Field>
                  <Field label="County"><Input value={form.county} onChange={(e) => set('county', e.target.value)} /></Field>
                  <Field label="Conference"><Input value={form.conference} onChange={(e) => set('conference', e.target.value)} /></Field>
                  <Field label="District"><Input value={form.district} onChange={(e) => set('district', e.target.value)} /></Field>
                  <div className="col-span-2"><Field label="Website"><Input value={form.website} onChange={(e) => set('website', e.target.value)} /></Field></div>
                  <div className="col-span-2"><Field label="Athletic website"><Input value={form.athletic_website} onChange={(e) => set('athletic_website', e.target.value)} /></Field></div>
                  <Field label="Primary color">
                    <div className="flex items-center gap-1.5">
                      <span className="h-5 w-5 shrink-0 rounded-sm border border-border" style={{ background: form.primary_color || '#fff' }} />
                      <Input value={form.primary_color} onChange={(e) => set('primary_color', e.target.value)} placeholder="#1d3557" />
                    </div>
                  </Field>
                  <Field label="Secondary color">
                    <div className="flex items-center gap-1.5">
                      <span className="h-5 w-5 shrink-0 rounded-sm border border-border" style={{ background: form.secondary_color || '#fff' }} />
                      <Input value={form.secondary_color} onChange={(e) => set('secondary_color', e.target.value)} placeholder="#f1faee" />
                    </div>
                  </Field>
                  <Field label="AD name"><Input value={form.ad_name} onChange={(e) => set('ad_name', e.target.value)} /></Field>
                  <Field label="AD email"><Input value={form.ad_email} onChange={(e) => set('ad_email', e.target.value)} /></Field>
                  <Field label="AD phone"><Input value={form.ad_phone} onChange={(e) => set('ad_phone', e.target.value)} /></Field>
                </div>
                <div className="mt-3 flex justify-end gap-2">
                  <Button variant="ghost" size="sm" disabled={busy} onClick={() => setEditing(false)}>Cancel</Button>
                  <Button size="sm" disabled={busy} onClick={saveEdit}>{busy ? 'Saving…' : 'Save changes'}</Button>
                </div>
              </Section>
            ) : (
              <Section title="Overview">
                <Row label="Address" value={[school.address, school.zip].filter(Boolean).join(', ') || undefined} />
                <Row label="Phone" value={school.phone} />
                <Row label="Website" value={<ExtLink href={school.website} />} />
                <Row label="Athletics" value={<ExtLink href={school.athletic_website} />} />
                <Row label="Conference" value={school.conference} />
                <Row label="District" value={school.district} />
                <Row label="Enrollment" value={school.enrollment != null ? String(school.enrollment) : undefined} />
                <Row label="Classification" value={school.classification} />
                <Row
                  label="Sports"
                  value={(school.sports_offered?.length ?? 0) > 0
                    ? `${school.sports_offered!.length} — ${school.sports_offered!.slice(0, 8).join(', ')}${school.sports_offered!.length > 8 ? '…' : ''}`
                    : undefined}
                />
                <Row label="Logo status" value={school.logo_status} />
                <Row
                  label="Verified"
                  value={school.last_verified_at ? <span title={school.last_verified_at}>{formatRelative(school.last_verified_at)}</span> : undefined}
                />
                <Row label="Scout ID" value={school.ghostfit_id && <span className="font-mono text-[10.5px]">{school.ghostfit_id}</span>} />
              </Section>
            )}

            <Section
              title="Athletic director"
              action={
                <Button variant="ghost" size="sm" disabled={busy} onClick={() => setAdFormOpen((v) => !v)}>
                  {adFormOpen ? 'Cancel' : 'Change AD…'}
                </Button>
              }
            >
              {ad?.name ? (
                <div className="text-[12px]">
                  <div className="font-medium">{ad.name}</div>
                  <div className="text-muted">
                    {[ad.email, ad.phone].filter(Boolean).join(' · ') || 'No contact info'}
                  </div>
                </div>
              ) : (
                <p className="text-[12px] text-subtle">No athletic director on record.</p>
              )}
              {adFormOpen && (
                <div className="mt-2.5 rounded-sm border border-border bg-bg/50 p-2.5">
                  <p className="mb-2 text-[11px] text-muted">
                    Archives the current AD to history, then sets the new one. Leave the name empty to record a departure with no replacement.
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    <Field label="New AD name"><Input value={adForm.name} onChange={(e) => setAdForm({ ...adForm, name: e.target.value })} /></Field>
                    <Field label="Email"><Input value={adForm.email} onChange={(e) => setAdForm({ ...adForm, email: e.target.value })} /></Field>
                    <Field label="Phone"><Input value={adForm.phone} onChange={(e) => setAdForm({ ...adForm, phone: e.target.value })} /></Field>
                    <Field label="Notes"><Input value={adForm.notes} onChange={(e) => setAdForm({ ...adForm, notes: e.target.value })} /></Field>
                  </div>
                  <div className="mt-2.5 flex justify-end">
                    <Button size="sm" disabled={busy} onClick={submitAdChange}>{busy ? 'Saving…' : 'Record AD change'}</Button>
                  </div>
                </div>
              )}
            </Section>

            <Section title={`Coaches (${coaches.length})`}>
              {coaches.length ? (
                <div className="space-y-0.5">
                  {coaches.map((c, i) => (
                    <div key={`${c.name}-${i}`} className="flex items-center gap-2 border-b border-divider py-1.5 text-[12px] last:border-b-0">
                      <div className="min-w-0 flex-1">
                        <div className="truncate font-medium">{c.name || 'Unnamed'}</div>
                        <div className="truncate text-[11px] text-muted">
                          {[c.sport || c.sport_id, c.role || c.title, c.email, c.phone].filter(Boolean).join(' · ') || '—'}
                        </div>
                      </div>
                      <Button variant="ghost" size="sm" disabled={busy || !c.name} onClick={() => departCoach(c)}>
                        Departed
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-[12px] text-subtle">No coaches on record.</p>
              )}
            </Section>

            <Section title={`History (${historyEntries.length})`}>
              {historyEntries.length ? (
                <div className="space-y-0.5">
                  {historyEntries.map(({ kind, e }, i) => (
                    <div key={i} className="border-b border-divider py-1.5 text-[12px] last:border-b-0">
                      <div className="flex items-center gap-2">
                        <Badge className="bg-bg text-muted">{kind}</Badge>
                        <span className="font-medium">{e.name || 'Unknown'}</span>
                        <span className="text-muted">{e.status || 'departed'}{e.moved_to_school_name ? ` → ${e.moved_to_school_name}` : ''}</span>
                        {e.departed_at && (
                          <span className="ml-auto text-[11px] text-subtle" title={e.departed_at}>{formatRelative(e.departed_at)}</span>
                        )}
                      </div>
                      {e.notes && <div className="mt-0.5 text-[11px] text-muted">{e.notes}</div>}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-[12px] text-subtle">No coach or AD changes recorded.</p>
              )}
            </Section>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
